<template>
	<div class="px-3 mt-4">
		<p class="pack-text text-grey-900">{{ content.planner_chooseFormula }}</p>
		<div class="d-flex align-items-center gap-2 d-none d-lg-flex">
			<div v-for="packObj in effectivePackagesToDisplay" :key="packObj.codePackage"
				@click="handlePackageChange(packObj)"
				class="package-tab d-flex align-items-center justify-content-between p-3 col" :class="isPackageTabActive(packObj.codePackage) ? 'active' : 'text-grey-900'
					">
				<span class="d-flex align-items-center">
					<template v-for="(component, componentIndex) in getPackageCompositionConfig(
						packObj.codePackage
					)" :key="`${packObj.codePackage}-${componentIndex}`">
						<span v-if="componentIndex > 0" class="mx-2 text-grey-900">+</span>
						<span class="d-flex align-items-center me-1 flex-column">
							<svg width="20" height="20" class="icon me-1">
								<use :xlink:href="`#___icon___${component.icon}`"></use>
							</svg>
							<span class="package-label">{{ getContentKey(component) }}</span>
						</span>
					</template>
				</span>

				<!-- Discount Badge -->
				<div class="d-flex justify-content-start align-items-center">
					<div v-if="showBadge(packObj.codePackage)
					" class="d-flex align-items-center justify-content-center prestation-discount-container f bg-primary-orange ms-1 ms-xl-3">
						<div class="prestation-discount subcaption-strong text-primary-white px-2 py-1 text-nowrap"
							v-bind:class="addDnoneClassIfABtest">
							{{ promoString.get(packObj.codePackage) }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- mobile -->
		<div class="mobile-packages-wrapper d-flex d-lg-none align-items-center justify-content-center mb-3 p-0 mt-3">
			<div @click.stop="handleMobileDropdownOpen"
				class="mobile-packages form-control py-3 cursor-pointer position-relative px-3 d-flex justify-content-between"
				:style="packagesMobileDropdownOpen && {
					'box-shadow': `0px 0px 4px 0px ${colors.lightBlue}`,
					color: colors.lightBlue,
				}
					" style="height: initial">
				<span class="font-weight-600 d-flex align-items-center"
					:class="getPackageCompositionConfig(packCode).length > 3 ? 'font-size-11' : getPackageCompositionConfig(packCode).length > 2 ? 'font-size-12' : 'font-size-14'">
					<template v-for="(component, componentIndex) in getPackageCompositionConfig(packCode)"
						:key="`mobile-header-${packCode}-${componentIndex}`">
						<span v-if="componentIndex > 0" class="mx-1 text-grey-900">+</span>
						<span class="d-inline-flex flex-column align-items-center">
							<svg width="20" height="20" class="icon mb-1">
								<use :xlink:href="`#___icon___${component.icon}`"></use>
							</svg>
							{{ getContentKey(component) }}
						</span>
					</template>
				</span>
				<span class="ml-2">
					<svg width="14" height="14" class="icon">
						<use data-v-7fb4dc0e="" xlink:href="#___icon___chevron-down-bold"></use>
					</svg>
				</span>
			</div>
		</div>
		<PackagesMobileDropdown @validateClick="(newPackCode) => handlePackageChange(newPackCode)" />
	</div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import PackagesMobileDropdown from "@/vue/planning/components/search/packagesMobileDropdown.vue"
import colors from "@/config/colors"
import { isABtest } from "@/utils/utils"
// import getPackageComposition from "../../logic/getPackageComposition"
import { isCS } from "@/vue/planning/logic/cs"
import packDisplayConfig from "@/config/packDisplayConfig"
import packageCompositionConfig, { PackageComponent } from "@/config/packageCompositionConfig"
import { airportCodeMap } from '@/config/airportCodeMap'
import flightPackCode from '@/config/flightPackCode'
import trainPackCode from '@/config/trainPackCode'
import { Transport } from '@/types'
import { getTrainPackageFamily, isSameTrainPackageType, resolveTrainPackCodeForOrigin, dedupeTrainPackagesForDisplay } from '@/config/trainPackageConfig'

// Computed
import packagesToDisplay from "@/vue/planning/computed/packagesToDisplay"
import getSiteFromDomain from '../../logic/getSiteFromDomain'
import displayPrice from '@/displayPrice/displayPrice'

type PackPromo = { perCent: number; perValue: number }

// Store
const store = useStore()

// Reactive data
const localPackagesToDisplay = ref<{ codePackage: string, packageId?: string }[]>([])

// Computed properties
const content = computed(() => store.state.content)
const packagesMobileDropdownOpen = computed(() => store.state.packagesMobileDropdownOpen)
const loading = computed(() => store.state.loading)
const packCode = computed(() => store.state.packCode)
const packageId = computed(() => store.state.packageId)
const dates = computed(() => store.state.dates)

// const maxPromosFromAvailablePackages = computed(() => store.getters.maxPromosFromAvailablePackages)
const availablePackCodes = computed(() => store.getters.availablePackCodes)

const isLocaleNL = computed(() => store.getters.isLocaleNL)
const isLocaleEN = computed(() => store.getters.isLocaleEN)

const effectivePackagesToDisplay = computed(() => {
	let result = [];

	if (localPackagesToDisplay.value && localPackagesToDisplay.value.length > 0) {
		result = localPackagesToDisplay.value;
	} else if (packagesToDisplay.value && packagesToDisplay.value.length > 0) {
		result = packagesToDisplay.value;
	} else if (availablePackCodes.value && availablePackCodes.value.length > 0) {
		result = availablePackCodes.value;
	}

	return dedupeTrainPackagesForDisplay(result);
})

const isPackageTabActive = (codePackage: string) => {
	return packCode.value === codePackage || isSameTrainPackageType(packCode.value, codePackage)
}

const promoString = computed(() => {
	const promoString: Map<string, string> = new Map();
	availablePackCodes.value.forEach((packObj: { codePackage: string, packageId?: string }) => {
		const familyPromo = getFamilyPromo(packObj.codePackage)
		if (getSiteFromDomain(window.location.hostname) === "www.uk.travelski.com") {
			promoString.set(packObj.codePackage, `-${displayPrice(familyPromo.perValue*100, store.getters.locale)}`);
		} else {
			promoString.set(packObj.codePackage, `-${familyPromo.perCent}%`);
		}
	});
	return promoString;
});

const promos = computed((): Record<string, PackPromo> => {
	const rawData = store.getters.getWiypCallByFamilyComposition(store.state.participants.join('-')) || {};
	const promoObject: Record<string, PackPromo> = {};
	if (rawData && typeof rawData === 'object') {
		availablePackCodes.value.forEach((packObj: { codePackage: string, packageId?: string }) => {
			// We NEED to load prestIdFusion/prestIdSource from prestmaps but we do not have them
		
			let maxPromo: PackPromo = { perCent: 0, perValue: 0 };
			// Loop through all values (usually just one key, but future-proof)
			// { "LON": { "PFF": {"1000000032": {wiypPrices: [{publicPriceWiyp: 100, sellingPriceWiyp: 90}]}}}}
			Object.values(rawData).forEach((valueOriginObject: any) => {
				/* 	"PFF": { "1000000031": { "wiypPrices": [{"sellingPriceWiyp": 5233.55,"publicPriceWiyp": 4951.33},{"sellingPriceWiyp": 4115.29,"publicPriceWiyp": 3916.55}]}}	*/
				Object.entries(valueOriginObject).forEach(([packageCode, valuePackageObject]: [string, any]) => {
					/* "1000000031": { "wiypPrices": [{"sellingPriceWiyp": 5233.55,"publicPriceWiyp": 4951.33},{"sellingPriceWiyp": 4115.29,"publicPriceWiyp": 3916.55}]} */
					if ( packageCode === packObj.codePackage && valuePackageObject && typeof valuePackageObject === 'object' ) {
						Object.values(valuePackageObject).forEach((valuePackageIdObject: any) => {
							/* "wiypPrices": [{"sellingPriceWiyp": 5233.55,"publicPriceWiyp": 4951.33},{"sellingPriceWiyp": 4115.29,"publicPriceWiyp": 3916.55}] */
							if (valuePackageIdObject && Array.isArray((valuePackageIdObject as any).wiypPrices)) {
								(valuePackageIdObject as any).wiypPrices.forEach((promoPrice: any) => {
									// need to check that prestId is in variant
									if (
										typeof promoPrice.publicPriceWiyp === 'number' &&
										typeof promoPrice.sellingPriceWiyp === 'number' &&
										promoPrice.publicPriceWiyp !== 0
									) {
										const promo = Math.round(
											((promoPrice.publicPriceWiyp - promoPrice.sellingPriceWiyp) /
												promoPrice.publicPriceWiyp) *
												100
										);
										if (promo > maxPromo.perCent) {
											maxPromo = { perCent: promo, perValue: Math.floor(promoPrice.publicPriceWiyp - promoPrice.sellingPriceWiyp)};
										}
									}
								});
							}	
						});
					}
				});
			});	
			promoObject[packObj.codePackage] = maxPromo;		
		})
	}

	return promoObject;
})


const addDnoneClassIfABtest = computed(() => {
	const dNoneClass = isABtest() ? "d-none !important" : ""
	return dNoneClass
})

const showBadge = (codePackage: string) => {
	const familyPromo = getFamilyPromo(codePackage)
	if (getSiteFromDomain(window.location.hostname) === "www.uk.travelski.com") {
		return familyPromo.perValue >= 100;
	}
	return familyPromo.perCent > 5;
}

const getFamilyPromo = (codePackage: string): PackPromo => {
	return getTrainPackageFamily(codePackage).reduce((best, code) => {
		const promo = promos.value[code] || { perCent: 0, perValue: 0 }
		return promo.perCent > best.perCent ? promo : best
	}, { perCent: 0, perValue: 0 })
}

// Methods
const handlePackageChange = async (packObj: { codePackage: string, packageId?: string }) => {
	const currentOrigin = store.state.flightOrigin?.split("-")[0] || ""
	const resolvedTrainPack = resolveTrainPackCodeForOrigin(packObj.codePackage, currentOrigin)
	if (
		packCode.value === packObj.codePackage ||
		(isSameTrainPackageType(packCode.value, packObj.codePackage) && packCode.value === resolvedTrainPack)
	) {
		store.dispatch("setPackagesMobileDropdownOpen", false)
		return
	}

	// Not anymore in use - use availability service instead
	// // First update package composition if needed
	// if (packObj.codePackage !== "HS" && !isCS(packObj)) {
	// 	await getPackageComposition(store)
	// }

	const noneOrigin = airportCodeMap.get("None")?.name
	const isCurrentOriginFlight =
		!!currentOrigin && currentOrigin !== noneOrigin && airportCodeMap.has(currentOrigin)
	const isFlightPackage = flightPackCode.includes(packObj.codePackage)
	const isTrainPackage = trainPackCode.includes(packObj.codePackage)
	// UK: flights and trains are limited to 9 guests. NL: only flights are (NL TNE trains allow 20).
	const shouldOpenParticipantsDropdown =
		((isLocaleEN.value && (isFlightPackage || isTrainPackage)) ||
			(isLocaleNL.value && isFlightPackage)) &&
		store.state.participants.length > 9

	// Truncate before setPackage so the single search uses max 9 and the packCode
	// watcher does not kick off a second search that races the loader.
	if (shouldOpenParticipantsDropdown) {
		store.commit("setParticipants", store.state.participants.slice(0, 9))
	}

	if (isFlightPackage) {
		await store.dispatch("setPackage", packObj.codePackage)
		store.commit("setPackageId", packObj.packageId)
		store.commit("setTransportv2", Transport.FLIGHT)
		store.commit("setTransportationSelected", true)
		if (!isCurrentOriginFlight) {
			await store.dispatch("selectDefaultFlightOrigin", { overwrite: true, forceFlightPackCode: true })
		}
	} else if (isTrainPackage) {
		store.commit("setTransportv2", Transport.TRAIN)
		store.commit("setTransportationSelected", true)
		await store.dispatch("setTrainPackageAndOrigin", packObj.codePackage)
	} else {
		await store.dispatch("setPackage", packObj.codePackage)
		store.commit("setPackageId", packObj.packageId)
		store.commit("setTransportv2", null)
		store.commit("setTransportationSelected", false)
		store.commit("setFlightOrigin", airportCodeMap.get("None")?.name)
	}

	if (shouldOpenParticipantsDropdown) {
		setTimeout(() => {
			store.dispatch("setParticipantsDropdownOpen", true)
		}, 0)
	}
}

const handleMobileDropdownOpen = () => {
	store.dispatch(
		"setPackagesMobileDropdownOpen",
		!packagesMobileDropdownOpen.value
	)
}

const getPackageCompositionConfig = (packageCode: string) => {
	const composition = packageCompositionConfig[packageCode]?.components || []
	return composition
}

const getContentKey = (component: PackageComponent) => {
	// Use short version for Dutch and English locales
	let keyToUse = component.contentKey;
	if ((isLocaleNL.value || isLocaleEN.value) && component.contentKeyShort) {
		keyToUse = component.contentKeyShort;
	}
	return content.value[keyToUse];
}

// Lifecycle
onMounted(() => {
	if ((!packagesToDisplay.value || packagesToDisplay.value.length === 0) &&
		availablePackCodes.value && availablePackCodes.value.length > 0) {
		localPackagesToDisplay.value = [...availablePackCodes.value]
	}
	store.dispatch("selectTransportWithDefaultPackage", store.state.transportationSelected);
})

// Watchers
watch(packCode, (newPackCode, oldPackCode) => {
	// Package code changed
}, { immediate: true })

watch(() => store.getters.transportv2, () => {
	localPackagesToDisplay.value = []
})



watch(availablePackCodes, (newVal, oldVal) => {
	// Always preserve the packages list if we have one, even if new value is empty
	if (newVal && newVal.length > 0) {
		// We have new packages, use them
		if ((!packagesToDisplay.value || packagesToDisplay.value.length === 0)) {
			// Get sorted packages from packDisplayConfig
			const sortedPackages = [...newVal].sort((a, b) => {
				// Use imported packDisplayConfig
				const aIndex = packDisplayConfig.indexOf(a.codePackage);
				const bIndex = packDisplayConfig.indexOf(b.codePackage);
				return aIndex - bIndex;
			});
			localPackagesToDisplay.value = sortedPackages;
		}
	} else if (newVal && newVal.length === 0 && localPackagesToDisplay.value && localPackagesToDisplay.value.length > 0) {
		// New value is empty but we have existing packages, keep the existing ones
		// Don't change localPackagesToDisplay - preserve current packages
	}
}, { immediate: true })
</script>

<style scoped>
.package-tab {
	height: 80px;
	border-radius: var(--border-radius-card);
	border: 1px solid var(--grey-300);
	cursor: pointer;
}

.package-tab.active {
	border: 1px solid var(--primary-blue);
}

.mobile-packages-wrapper,
.mobile-packages {
	border-radius: 8px;
}

.prestation-discount-container {
	border-radius: 37px;
}

.package-label {
	white-space: nowrap;
}

.pack-text {
	font-size: 14px;
}

.font-size-11 {
	font-size: 11px;
}

.font-size-12 {
	font-size: 12px;
}

.font-size-14 {
	font-size: 14px;
}
</style>
